import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { createTransitionResume, updateResumeData } from '../../../../services/resume.service';
import { toast } from 'react-hot-toast';
import { mergeAdditionalExperiences, createAdditionalExperiencePrompt } from '../../../../utils/resumeDataMerger';
import CreditBadge from '../common/CreditBadge';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import TextField from '@mui/material/TextField';

dayjs.extend(customParseFormat);

const MonthYearPicker = ({ value, onChange, label, disabled, isPresent, onPresentChange, error }) => {
  const dateValue = value && value !== 'Present' ? dayjs(value, 'MM/YYYY') : null;

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest font-montserrat">{label}</label>
        {onPresentChange && (
          <label className="flex items-center gap-2 cursor-pointer group">
            <input 
              type="checkbox" 
              checked={isPresent} 
              onChange={(e) => onPresentChange(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue focus:ring-blue transition-all"
            />
            <span className="text-[10px] font-bold text-slate-400 group-hover:text-blue transition-colors uppercase tracking-tight">Currently Here</span>
          </label>
        )}
      </div>
      <div className="relative">
        <DatePicker
          views={['year', 'month']}
          openTo="month"
          value={dateValue}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onChange(newValue.format('MM/YYYY'));
            }
          }}
          disabled={disabled || isPresent}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              error={!!error}
              inputProps={{
                ...params.inputProps,
                placeholder: isPresent ? 'Present' : 'MM/YYYY',
                readOnly: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: isPresent ? '#f1f5f9' : '#f8fafc',
                  fontFamily: 'Inter, sans-serif',
                  '& fieldset': { 
                    borderColor: '#e2e8f0',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': { borderColor: '#3b82f6' },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#3b82f6',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '12px 16px',
                  color: isPresent ? '#94a3b8' : '#1e293b',
                  fontWeight: isPresent ? '700' : '400',
                  fontStyle: isPresent ? 'italic' : 'normal',
                },
                '& .MuiInputAdornment-root': {
                  marginRight: '8px'
                }
              }}
            />
          )}
        />
      </div>
    </div>
  );
};

// Helpers for splitting/joining dates
const parseDates = (dateStr) => {
  if (!dateStr) return { start: '', end: '', isPresent: false };
  const parts = dateStr.split(/[–-]/).map(s => s.trim());
  if (parts.length <= 1) return { start: parts[0] || '', end: '', isPresent: false };
  return { 
    start: parts[0], 
    end: parts[1] === 'Present' ? '' : parts[1], 
    isPresent: parts[1] === 'Present' 
  };
};

const joinDates = (start, end, isPresent) => {
  if (!start) return ''; // Strictly empty string to trigger validation failure
  const endValue = isPresent ? 'Present' : (end || '');
  return endValue ? `${start} – ${endValue}` : start;
};

const ExperienceBuilderStep = ({ onNavigate, onDataUpdate, uploadedFile, reviewedData, targetRole, jobDescription, additionalInfo, selectedExperiences = [], gapAnalysis, currentExperienceIndex = 0, experienceEntries = {} }) => {
  const currentExperienceType = selectedExperiences[currentExperienceIndex];
  const experienceConfigs = {
    freelancing: {
      title: 'Freelancing & Consulting', icon: 'handshake', allowMultiple: true, fields: [
        { name: 'projectTitle', label: 'Project Title', placeholder: 'e.g., Marketing Strategy Consulting', required: true },
        { name: 'client', label: 'Client/Company', placeholder: 'e.g., Tech Startup Inc.', required: false },
        { name: 'duration', label: 'Duration (MM/YYYY or Present)', placeholder: 'e.g., 01/2023 - 03/2024', required: true },
        { name: 'skills', label: 'Core Skills Used', placeholder: 'e.g., Project Management, SEO, Analytics', required: true }
      ]
    },
    internship: {
      title: 'Internship', icon: 'school', allowMultiple: true, fields: [
        { name: 'company', label: 'Company', placeholder: 'e.g., Google', required: true },
        { name: 'position', label: 'Position', placeholder: 'e.g., Product Management Intern', required: true },
        { name: 'duration', label: 'Duration (MM/YYYY or Present)', placeholder: 'e.g., Summer 2023', required: true },
        { name: 'skills', label: 'Core Skills Used', placeholder: 'e.g., User Research, Analysis', required: true }
      ]
    },
    probono: {
      title: 'Pro-bono Work', icon: 'volunteer_activism', allowMultiple: true, fields: [
        { name: 'organization', label: 'Organization', placeholder: 'e.g., Local Non-Profit', required: true },
        { name: 'role', label: 'Role', placeholder: 'e.g., Project Coordinator', required: true },
        { name: 'duration', label: 'Duration (MM/YYYY or Present)', placeholder: 'e.g., Jan 2023 - Jun 2023', required: true },
        { name: 'skills', label: 'Core Skills', placeholder: 'e.g., Leadership, Planning', required: true }
      ]
    },
    certification: {
      title: 'Certifications', icon: 'verified', allowMultiple: true, fields: [
        { name: 'certificationName', label: 'Certification Name', placeholder: 'e.g., Google PM Certificate', required: true },
        { name: 'issuer', label: 'Issuing Organization', placeholder: 'e.g., Google / Coursera', required: true },
        { name: 'completionDate', label: 'Completion Date (MM/YYYY or Present)', placeholder: 'e.g., 12/2023', required: true },
        { name: 'relevantSkills', label: 'Skills Gained', placeholder: 'e.g., Strategy, Agile', required: false }
      ]
    },
    minortasks: {
      title: 'Relevant Org Tasks', icon: 'assignment', allowMultiple: true, fields: [
        { name: 'taskDescription', label: 'Task Description', placeholder: 'e.g., Led agile sprint planning', required: true },
        { name: 'organization', label: 'Organization', placeholder: 'e.g., Previous Company', required: true },
        { name: 'timeframe', label: 'Timeframe (MM/YYYY or Present)', placeholder: 'e.g., 04/2023', required: true },
        { name: 'skills', label: 'Demonstrated Skills', placeholder: 'e.g., Agile, Stakeholder Management', required: true }
      ]
    },
    portfolio: {
      title: 'Personal Projects', icon: 'folder_special', allowMultiple: true, fields: [
        { name: 'projectName', label: 'Project Name', placeholder: 'e.g., E-commerce Redesign', required: true },
        { name: 'description', label: 'Brief Description', placeholder: 'e.g., Improved checkout flow', required: true },
        { name: 'duration', label: 'Duration (MM/YYYY or Present)', placeholder: 'e.g., 3 months', required: true },
        { name: 'skills', label: 'Skills Used', placeholder: 'e.g., UX, Figma, Testing', required: true }
      ]
    }
  };
  const currentConfig = experienceConfigs[currentExperienceType];

  const [generatingBullets, setGeneratingBullets] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Initialize React Hook Form
  const { register, control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      entries: {
        ...experienceEntries,
        [currentExperienceType]: experienceEntries[currentExperienceType] || [{}]
      }
    }
  });

  useEffect(() => {
    reset({ entries: experienceEntries });
  }, [experienceEntries, reset]);

  const handleAddAnother = () => {
    const updatedEntries = { ...experienceEntries };
    if (!updatedEntries[currentExperienceType]) updatedEntries[currentExperienceType] = [];
    updatedEntries[currentExperienceType].push({});
    onDataUpdate({ experienceEntries: updatedEntries });
  };

  const handleRemoveEntry = (idx) => {
    const updatedEntries = { ...experienceEntries };
    updatedEntries[currentExperienceType] = updatedEntries[currentExperienceType].filter((_, i) => i !== idx);
    onDataUpdate({ experienceEntries: updatedEntries });
  };

  const onInvalid = (errs) => {
    toast.error('Please fix the required fields before continuing.', {
      duration: 3000,
      position: 'top-center'
    });
  };

  const onSubmit = async (data) => {
    // Extra guard for empty entries list
    const currentEntries = data.entries?.[currentExperienceType] || [];
    if (currentEntries.length === 0) {
      toast.error(`Please add at least one ${currentConfig?.title || 'entry'}`, {
        duration: 3000,
        position: 'top-center',
        style: { borderRadius: '12px', background: '#1e293b', color: '#fff' }
      });
      return;
    }

    const updatedEntries = data.entries;
    
    if (currentExperienceIndex < selectedExperiences.length - 1) {
      // Trigger a save by using onNavigate to the same step but with new data
      onNavigate('experience-builder', { 
        experienceEntries: updatedEntries, 
        currentExperienceIndex: currentExperienceIndex + 1 
      });
    } else {
      setProcessing(true);
      try {
        const userId = localStorage.getItem('user_id');
        const category = localStorage.getItem('category');
        const enhancedReviewedData = mergeAdditionalExperiences(reviewedData, updatedEntries);
        const response = await createTransitionResume({ 
          filename: uploadedFile.filename || uploadedFile.file?.filename, 
          reviewedData: enhancedReviewedData, 
          targetRole, 
          jobDescription, 
          additionalInfo: additionalInfo + createAdditionalExperiencePrompt(updatedEntries),
          user_id: userId,
          category: category,
          isManual: false
        });

        // ─── FIX Bug 2: Persist the AI-generated resumeData to DB immediately ───
        const generatedData = response.result || response;
        const resumeId = uploadedFile?.id;
        if (resumeId && generatedData.resumeData) {
          try {
            await updateResumeData(resumeId, {
              ...generatedData.resumeData,
              step: 'preview',
              targetRole,
              jobDescription
            });
          } catch (dbErr) {
            console.warn('Failed to persist generated resumeData to DB:', dbErr.message);
          }
        }

        // ─── FIX Bug 1: Always attach uploadedFile.id so PreviewStep can update the record ───
        const enrichedResult = {
          ...generatedData,
          id: generatedData.id || resumeId,
          resume_id: generatedData.resume_id || resumeId,
        };

        onNavigate('preview', { 
          result: enrichedResult, 
          type: 'create', 
          targetRole, 
          reviewedData: enhancedReviewedData, 
          uploadedFile, 
          hasAdditionalExperiences: true, 
          gapAnalysis 
        });
      } catch (error) {
        toast.error('Failed to generate resume.');
      } finally {
        setProcessing(false);
      }
    }
  };

  const generateBullets = async (idx) => {
    const entry = watch(`entries.${currentExperienceType}.${idx}`);
    if (!entry || Object.keys(entry).length < 2) {
      toast.error('Fill in basic details first.');
      return;
    }
    setGeneratingBullets(true);
    setTimeout(() => {
      let bullets = [`Spearheaded initiatives to drive results`, `Collaborated on ${targetRole}-aligned projects`, `Optimized internal processes through strategic vision`];
      const achievements = '• ' + bullets.join('\n• ');
      
      const updatedEntries = { ...experienceEntries };
      if (!updatedEntries[currentExperienceType]) updatedEntries[currentExperienceType] = [];
      updatedEntries[currentExperienceType][idx] = { ...updatedEntries[currentExperienceType][idx], achievements };
      onDataUpdate({ experienceEntries: updatedEntries });
      setGeneratingBullets(false);
    }, 1500);
  };

  const handleSkip = async () => {
    // We intentionally don't add the current experience type data to updatedEntries
    const updatedEntries = { ...experienceEntries };
    
    if (currentExperienceIndex < selectedExperiences.length - 1) {
      onNavigate('experience-builder', { 
        experienceEntries: updatedEntries, 
        currentExperienceIndex: currentExperienceIndex + 1 
      });
    } else {
      setProcessing(true);
      try {
        const userId = localStorage.getItem('user_id');
        const category = localStorage.getItem('category');
        const enhancedReviewedData = mergeAdditionalExperiences(reviewedData, updatedEntries);
        const response = await createTransitionResume({ 
          filename: uploadedFile.filename || uploadedFile.file?.filename, 
          reviewedData: enhancedReviewedData, 
          targetRole, 
          jobDescription, 
          additionalInfo: additionalInfo + createAdditionalExperiencePrompt(updatedEntries),
          user_id: userId,
          category: category,
          isManual: false
        });

        // ─── FIX Bug 2: Persist the AI-generated resumeData to DB immediately ───
        const generatedData = response.result || response;
        const resumeId = uploadedFile?.id;
        if (resumeId && generatedData.resumeData) {
          try {
            await updateResumeData(resumeId, {
              ...generatedData.resumeData,
              step: 'preview',
              targetRole,
              jobDescription
            });
          } catch (dbErr) {
            console.warn('Failed to persist generated resumeData to DB:', dbErr.message);
          }
        }

        // ─── FIX Bug 1: Always attach uploadedFile.id so PreviewStep can update the record ───
        const enrichedResult = {
          ...generatedData,
          id: generatedData.id || resumeId,
          resume_id: generatedData.resume_id || resumeId,
        };

        onNavigate('preview', { 
          result: enrichedResult, 
          type: 'create', 
          targetRole, 
          reviewedData: enhancedReviewedData, 
          uploadedFile, 
          hasAdditionalExperiences: true, 
          gapAnalysis 
        });
      } catch (error) {
        toast.error('Failed to generate resume.');
      } finally {
        setProcessing(false);
      }
    }
  };

  const progressPercentage = ((currentExperienceIndex + 1) / selectedExperiences.length) * 100;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <div className="w-[38px] h-[38px] bg-navy rounded-[10px] flex items-center justify-center">
              <span className="msi text-white">article</span>
            </div>
            <span className="font-montserrat font-extrabold text-xl text-navy tracking-[-0.4px]">Accredian AI</span>
          </div>
          <CreditBadge />
        </div>
      </nav>

      <div className="max-w-[1000px] mx-auto px-8 py-10 w-full flex-1">
        <form onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-blue-light text-blue flex items-center justify-center">
                <span className="msi text-[32px]">{currentConfig?.icon}</span>
              </div>
              <div>
                <h1 className="text-[28px] font-extrabold text-navy font-montserrat">{currentConfig?.title}</h1>
                <p className="text-[14px] text-slate-500">Step {currentExperienceIndex + 1} of {selectedExperiences.length}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${progressPercentage}%` }}></div>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            {(experienceEntries[currentExperienceType] || [{}]).map((_, idx) => (
              <div key={idx} className="bg-white border border-slate-200 rounded-3xl p-8 relative">
                {idx > 0 && (
                  <button type="button" className="absolute top-8 right-8 text-slate-300 hover:text-premium-red" onClick={() => handleRemoveEntry(idx)}>
                    <span className="msi text-[18px]">delete_sweep</span>
                  </button>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  {currentConfig?.fields.map(f => {
                    const isDate = f.name.toLowerCase().includes('date') || f.name.toLowerCase().includes('duration') || f.name.toLowerCase().includes('timeframe');
                    return (
                      <div key={f.name} className={f.name === 'description' || f.name === 'taskDescription' ? 'md:col-span-2' : ''}>
                        {isDate ? (
                          <Controller
                            control={control}
                            name={`entries.${currentExperienceType}.${idx}.${f.name}`}
                            rules={{ required: `${f.label} is required` }}
                            render={({ field: dateField, fieldState: { error } }) => {
                              const d = parseDates(dateField.value);
                              return (
                                <div className="flex flex-col gap-1">
                                  <div className="grid grid-cols-2 gap-4">
                                    <MonthYearPicker 
                                      label="Start Date *"
                                      value={d.start}
                                      error={!!error}
                                      onChange={(v) => dateField.onChange(joinDates(v, d.end, d.isPresent))}
                                    />
                                    <MonthYearPicker 
                                      label="End Date *"
                                      value={d.end}
                                      error={!!error}
                                      isPresent={d.isPresent}
                                      onPresentChange={(c) => dateField.onChange(joinDates(d.start, d.end, c))}
                                      onChange={(v) => dateField.onChange(joinDates(d.start, v, false))}
                                    />
                                  </div>
                                  {error && <p className="text-premium-red text-[11px] font-bold mt-1 font-inter">{error.message}</p>}
                                </div>
                              );
                            }}
                          />
                        ) : (
                          <>
                            <label className="block text-[11px] font-extrabold text-slate-400 uppercase tracking-widest mb-2 font-montserrat">{f.label} {f.required && '*'}</label>
                            <input
                              type="text"
                              {...register(`entries.${currentExperienceType}.${idx}.${f.name}`, { 
                                required: f.required ? `${f.label} is required` : false
                              })}
                              placeholder={f.placeholder}
                              className={`w-full px-4 py-3 bg-slate-50 border rounded-xl text-sm focus:outline-none focus:border-blue focus:bg-white focus:ring-4 focus:ring-blue-light ${errors.entries?.[currentExperienceType]?.[idx]?.[f.name] ? 'border-premium-red' : 'border-slate-200'}`}
                            />
                            {errors.entries?.[currentExperienceType]?.[idx]?.[f.name] && (
                              <p className="text-premium-red text-[11px] font-bold mt-1">{errors.entries[currentExperienceType][idx][f.name].message}</p>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                  <div className="flex items-center justify-between mb-4">
                    <label className="block text-[11px] font-extrabold text-slate-500 uppercase tracking-widest font-montserrat">Significant Achievements *</label>
                    {/* <button type="button" className="text-blue text-[11px] font-bold" onClick={() => generateBullets(idx)}>
                      <span className="msi text-[16px] align-middle">auto_awesome</span> Smart Generate
                    </button> */}
                  </div>
                  <textarea
                    {...register(`entries.${currentExperienceType}.${idx}.achievements`, { required: 'Achievements are required' })}
                    placeholder="Describe your impact here..."
                    className={`w-full px-4 py-4 bg-white border rounded-2xl text-sm min-h-[160px] focus:outline-none focus:border-blue ${errors.entries?.[currentExperienceType]?.[idx]?.achievements ? 'border-premium-red' : 'border-slate-200'}`}
                  />
                  {errors.entries?.[currentExperienceType]?.[idx]?.achievements && (
                    <p className="text-premium-red text-[11px] font-bold mt-1">Please provide at least one achievement.</p>
                  )}
                </div>
              </div>
            ))}

            <button type="button" className="w-full h-16 border-2 border-dashed border-slate-200 rounded-3xl text-slate-400 font-bold uppercase transition-all hover:border-blue hover:text-blue" onClick={handleAddAnother}>
              + Add another {currentConfig?.title}
            </button>
          </div>

          <div className="flex items-center justify-between mt-12 pt-8 border-t border-slate-200">
            <button type="button" className="h-[52px] px-8 bg-white border border-slate-200 text-navy font-bold rounded-xl hover:bg-slate-50 transition-colors" onClick={() => currentExperienceIndex > 0 ? onDataUpdate({ currentExperienceIndex: currentExperienceIndex - 1 }) : onNavigate('gap-analysis')}>
              Go Back
            </button>
            <div className="flex items-center gap-4">
              <button type="button" className="h-[52px] px-8 bg-white border border-slate-200 text-slate-500 font-bold rounded-xl hover:bg-slate-50 hover:text-slate-700 transition-colors" onClick={handleSkip} disabled={processing}>
                Skip
              </button>
              <button type="submit" className="h-[52px] px-12 bg-blue-500 text-white font-bold rounded-xl shadow-lg hover:bg-[#0055DD]" disabled={processing}>
                {processing ? 'Processing...' : (currentExperienceIndex < selectedExperiences.length - 1 ? 'Next Experience' : 'Create Resume')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    </LocalizationProvider>
  );
};

export default ExperienceBuilderStep;
